import { ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, Input } from '../ui/kit';
import { Logo } from '../ui/Logo';

export function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card className="flex w-full max-w-md flex-col gap-5 p-7">
        <div className="flex items-center gap-4">
          <Logo height={48} />
          <div className="border-l border-border pl-4">
            <h1 className="text-xl font-extrabold">{title}</h1>
            <p className="text-sm text-muted">{subtitle}</p>
          </div>
        </div>
        {children}
      </Card>
    </div>
  );
}

export function Login({ area }: { area: string }) {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string>();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const run = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try { await fn(); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Shell title={`Empalme Más Cerca · ${area}`} subtitle="Acceso exclusivo para el personal comunal">
      {!mfaToken ? (
        <form className="flex flex-col gap-4" onSubmit={run(async () => { const r = await auth.login(email.trim(), password); if (r) setMfaToken(r.mfaToken); })}>
          <Input label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Contraseña" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} error={error} />
          <Button type="submit" loading={busy}>Ingresar</Button>
          <a href="/recuperar" className="text-center text-sm font-semibold text-link hover:underline">¿Olvidaste tu contraseña?</a>
        </form>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={run(() => auth.verifyMfa(mfaToken, code))}>
          <p className="text-sm">Ingresá el código de 6 dígitos de tu aplicación de autenticación.</p>
          <Input label="Código de seguridad" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus required
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} error={error} />
          <Button type="submit" loading={busy} disabled={code.length !== 6}>Verificar</Button>
          <Button type="button" kind="ghost" onClick={() => { setMfaToken(undefined); setCode(''); }}>Volver</Button>
        </form>
      )}
    </Shell>
  );
}

/** Primer ingreso al panel: activar el segundo factor es obligatorio. */
export function MfaSetup() {
  const auth = useAuth();
  const [data, setData] = useState<{ secret: string; qr: string }>();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void auth.setupMfa()
      .then(async (s) => {
        const QRCode = (await import('qrcode')).default;
        setData({ secret: s.secret, qr: await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 220 }) });
      })
      .catch((e) => setError(errorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try { await auth.confirmMfa(code); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Shell title="Activá el segundo factor" subtitle="Protege el panel aunque alguien conozca tu contraseña">
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
        <li>Instalá una app de autenticación (Google Authenticator, Microsoft Authenticator o similar).</li>
        <li>Escaneá este código con la app.</li>
        <li>Escribí el código de 6 dígitos que te muestra.</li>
      </ol>
      {data ? (
        <div className="flex flex-col items-center gap-2">
          <img src={data.qr} alt="Código QR para configurar la app de autenticación" className="rounded-xl border border-border bg-white p-2" width={220} height={220} />
          <p className="text-xs text-muted">¿No podés escanear? Ingresá esta clave a mano:</p>
          <code data-testid="mfa-secret" className="rounded-lg bg-surface-2 px-3 py-1.5 text-sm tracking-wider break-all">{data.secret}</code>
        </div>
      ) : null}
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input label="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} error={error} />
        <Button type="submit" icon={<ShieldCheck className="size-4" />} loading={busy} disabled={code.length !== 6}>Activar y entrar</Button>
        <Button type="button" kind="ghost" onClick={() => void auth.logout()}>Cancelar</Button>
      </form>
    </Shell>
  );
}
